import { useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { dailyVisitorData, hourlyTrafficData } from '../../../mocks/dashboard';

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border rounded-xl p-3 shadow-hover text-[12px]">
      <p className="font-semibold text-charcoal-500 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color }} className="font-medium">{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

export function DailyVisitorsChart() {
  const [range, setRange] = useState<'7' | '30' | '90'>('7');
  const data = range === '7' ? dailyVisitorData.slice(-7) : dailyVisitorData;

  return (
    <div className="bg-white rounded-xl2 shadow-card p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-[16px] font-semibold text-charcoal-600">Günlük Ziyaretçi Trendi</h3>
          <p className="text-[12px] text-charcoal-300 mt-0.5">Toplam ve tekil ziyaretçi sayıları</p>
        </div>
        <div className="flex gap-1 bg-[#FAF8F4] rounded-full p-1">
          {(['7', '30', '90'] as const).map(r => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1 rounded-full text-[12px] font-medium transition-all cursor-pointer whitespace-nowrap
                ${range === r ? 'bg-white text-champagne-600 shadow-sm' : 'text-charcoal-400 hover:text-charcoal-600'}
              `}
            >
              {r}G
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="gradVisitors" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradUnique" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F4C2C2" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#F4C2C2" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#A0A0A0' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#A0A0A0' }} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip />} />
          <Area type="monotone" dataKey="visitors" name="Toplam" stroke="#D4AF37" strokeWidth={2.5} fill="url(#gradVisitors)" dot={false} />
          <Area type="monotone" dataKey="unique" name="Tekil" stroke="#F4C2C2" strokeWidth={2} fill="url(#gradUnique)" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-4 mt-3">
        <div className="flex items-center gap-1.5 text-[11px] text-charcoal-400">
          <span className="w-3 h-0.5 bg-champagne-500 rounded-full inline-block" />
          Toplam Ziyaretçi
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-charcoal-400">
          <span className="w-3 h-0.5 bg-blush-300 rounded-full inline-block" />
          Tekil Ziyaretçi
        </div>
      </div>
    </div>
  );
}

export function HourlyTrafficChart() {
  return (
    <div className="bg-white rounded-xl2 shadow-card p-6">
      <div className="mb-5">
        <h3 className="text-[16px] font-semibold text-charcoal-600">Saatlik Trafik Dağılımı</h3>
        <p className="text-[12px] text-charcoal-300 mt-0.5">Bugünkü saatlik ziyaret yoğunluğu</p>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={hourlyTrafficData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#D4AF37" />
              <stop offset="100%" stopColor="#E8C87C" />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" vertical={false} />
          <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#A0A0A0' }} axisLine={false} tickLine={false} interval={3} />
          <YAxis tick={{ fontSize: 11, fill: '#A0A0A0' }} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: '#FAF8F4' }} />
          <Bar dataKey="visits" name="Ziyaret" fill="url(#barGrad)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
